import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, GraduationCap, Tag, Sparkles, AlertTriangle, ChevronDown, ChevronRight } from "lucide-react";

export default function Courses() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [officialName, setOfficialName] = useState("");
  const [aliases, setAliases] = useState([]);
  const [aliasInput, setAliasInput] = useState("");
  const [suspOpen, setSuspOpen] = useState(false);

  const { data: courses, isLoading } = useQuery({
    queryKey: ["courses"],
    queryFn: async () => (await api.get("/courses")).data,
  });

  const { data: campaigns } = useQuery({
    queryKey: ["campaigns"],
    queryFn: async () => (await api.get("/courses/campaigns")).data,
  });

  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  useEffect(() => {
    if (!open || aliases.length === 0) { setPreview(null); return; }
    let active = true;
    setPreviewLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await api.post("/courses/preview", { official_name: officialName, keywords: aliases });
        if (active) setPreview(res.data);
      } catch { if (active) setPreview(null); }
      finally { if (active) setPreviewLoading(false); }
    }, 350);
    return () => { active = false; clearTimeout(t); };
  }, [aliases, officialName, open]);

  const reset = () => { setEditing(null); setOfficialName(""); setAliases([]); setAliasInput(""); };

  const openCreate = () => { reset(); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c); setOfficialName(c.official_name); setAliases(c.aliases || []); setAliasInput("");
    setOpen(true);
  };

  const save = useMutation({
    mutationFn: async () => {
      const payload = { official_name: officialName, aliases };
      if (editing) return (await api.put(`/courses/${editing.id}`, payload)).data;
      return (await api.post("/courses", payload)).data;
    },
    onSuccess: () => {
      toast.success(editing ? "Corso aggiornato" : "Corso creato");
      qc.invalidateQueries({ queryKey: ["courses"] });
      qc.invalidateQueries({ queryKey: ["campaigns"] });
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      setOpen(false); reset();
    },
    onError: () => toast.error("Errore durante il salvataggio"),
  });

  const remove = useMutation({
    mutationFn: async (cid) => (await api.delete(`/courses/${cid}`)).data,
    onSuccess: () => {
      toast.success("Corso eliminato");
      qc.invalidateQueries({ queryKey: ["courses"] });
      qc.invalidateQueries({ queryKey: ["campaigns"] });
      qc.invalidateQueries({ queryKey: ["leads"] });
    },
    onError: () => toast.error("Errore durante l'eliminazione"),
  });

  const addAlias = () => {
    const v = aliasInput.trim();
    if (v && !aliases.includes(v)) setAliases((a) => [...a, v]);
    setAliasInput("");
  };

  // Quick-assign of unrecognized campaigns to an existing course.
  const [assignSel, setAssignSel] = useState({});
  const assign = useMutation({
    mutationFn: async ({ campaign, course_id }) =>
      (await api.post("/courses/assign-campaign", { campaign, course_id })).data,
    onSuccess: (_d, vars) => {
      toast.success("Campagna assegnata al corso");
      setAssignSel((s) => { const n = { ...s }; delete n[vars.campaign]; return n; });
      qc.invalidateQueries({ queryKey: ["courses"] });
      qc.invalidateQueries({ queryKey: ["campaigns"] });
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: () => toast.error("Errore durante l'assegnazione"),
  });

  const ignore = useMutation({
    mutationFn: async (campaign) =>
      (await api.post("/courses/campaigns/ignore", { campaign })).data,
    onSuccess: () => {
      toast.success("Campagna rimossa dai sospesi");
      qc.invalidateQueries({ queryKey: ["campaigns"] });
    },
    onError: () => toast.error("Errore durante la rimozione"),
  });

  const unrecognized = (campaigns || []).filter((c) => c.campaign && !c.recognized && !c.ignored);

  return (
    <div className="space-y-5" data-testid="courses-page">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Aggiungi i corsi con delle <strong>parole chiave</strong>: il sistema riconosce il corso quando la campagna dell'email contiene quelle parole. Più parole nella stessa regola = devono esserci tutte (es. "carbon farming"); basta una regola qualsiasi a riconoscere il corso.
        </p>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
          <DialogTrigger asChild>
            <Button data-testid="new-course-button" className="gap-2" onClick={openCreate}>
              <Plus className="h-4 w-4" /> Nuovo corso
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="course-dialog">
            <DialogHeader>
              <DialogTitle className="font-display tracking-tight">{editing ? "Modifica corso" : "Nuovo corso"}</DialogTitle>
              <DialogDescription>
                Definisci il nome ufficiale e le parole chiave. Il corso viene riconosciuto quando la campagna dell'email contiene le parole di almeno una regola.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Nome ufficiale</Label>
                <Input
                  className="mt-1"
                  data-testid="course-name-input"
                  placeholder="Es. Carbon Farming e Crediti di Carbonio"
                  value={officialName}
                  onChange={(e) => setOfficialName(e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Parole chiave</Label>
                <p className="mt-1 text-xs text-muted-foreground">
                  Digita una o più parole per regola (es. <code>cam</code>, oppure <code>carbon farming</code>) e premi Aggiungi. In una regola con più parole devono essere presenti <strong>tutte</strong>.
                </p>
                <div className="mt-2 flex gap-2">
                  <Input
                    data-testid="alias-input"
                    placeholder="Es. carbon farming"
                    value={aliasInput}
                    onChange={(e) => setAliasInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addAlias(); } }}
                  />
                  <Button type="button" variant="outline" data-testid="add-alias-button" onClick={addAlias}>Aggiungi</Button>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {aliases.map((a) => (
                    <Badge key={a} variant="secondary" className="gap-1 rounded-full py-1 pl-3 pr-1">
                      {a}
                      <button className="ml-1 rounded-full p-0.5 hover:bg-slate-300" onClick={() => setAliases((x) => x.filter((y) => y !== a))}>
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                  {aliases.length === 0 && <span className="text-xs text-muted-foreground">Nessuna parola chiave aggiunta</span>}
                </div>

                {aliases.length > 0 && (
                  <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-3" data-testid="course-preview">
                    <div className="flex items-center gap-2 text-sm font-semibold text-blue-800">
                      <Sparkles className="h-4 w-4" />
                      {previewLoading ? "Calcolo corrispondenze…" : `${preview?.matched_leads ?? 0} lead corrispondenti`}
                    </div>
                    {preview?.matched_campaigns?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {preview.matched_campaigns.slice(0, 12).map((m) => (
                          <span key={m.campaign} className="rounded-full bg-white px-2 py-0.5 text-xs text-blue-700 ring-1 ring-blue-200">
                            {m.campaign} · {m.count}
                          </span>
                        ))}
                      </div>
                    )}
                    {!previewLoading && (preview?.matched_leads ?? 0) === 0 && (
                      <p className="mt-1 text-xs text-blue-700">Nessuna campagna esistente corrisponde a queste parole chiave.</p>
                    )}
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setOpen(false); reset(); }}>Annulla</Button>
              <Button data-testid="save-course-button" onClick={() => save.mutate()} disabled={!officialName.trim() || save.isPending}>
                {editing ? "Salva" : "Crea corso"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-40 w-full" />)}
        </div>
      ) : courses?.length ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {courses.map((c) => (
            <Card key={c.id} className="shadow-sm" data-testid={`course-card-${c.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex h-9 w-9 items-center justify-center rounded-md bg-blue-50 text-blue-700">
                    <GraduationCap className="h-5 w-5" />
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" aria-label="Modifica" onClick={() => openEdit(c)} data-testid={`edit-course-${c.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" aria-label="Elimina" onClick={() => remove.mutate(c.id)} data-testid={`delete-course-${c.id}`}>
                      <Trash2 className="h-4 w-4 text-rose-600" />
                    </Button>
                  </div>
                </div>
                <h3 className="mt-3 font-display text-base font-semibold tracking-tight">{c.official_name}</h3>
                <p className="mt-1 text-xs text-muted-foreground">{c.lead_count} lead collegati</p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {(c.aliases || []).map((a) => (
                    <span key={a} className="rounded-full bg-secondary px-2.5 py-0.5 text-xs text-secondary-foreground">{a}</span>
                  ))}
                  {(!c.aliases || c.aliases.length === 0) && <span className="text-xs text-muted-foreground">Nessuna parola chiave</span>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="shadow-sm">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <GraduationCap className="mb-3 h-10 w-10 text-muted-foreground" />
            <p className="font-medium">Nessun corso ancora creato</p>
            <p className="mt-1 text-sm text-muted-foreground">Crea il primo corso per riconoscere automaticamente i lead in arrivo.</p>
          </CardContent>
        </Card>
      )}

      {unrecognized.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/50 shadow-sm" data-testid="suspended-campaigns-panel">
          <button
            type="button"
            data-testid="suspended-toggle"
            onClick={() => setSuspOpen((o) => !o)}
            className="flex w-full items-center gap-2 p-5 text-left"
          >
            {suspOpen ? <ChevronDown className="h-4 w-4 text-amber-600" /> : <ChevronRight className="h-4 w-4 text-amber-600" />}
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <h3 className="font-display text-base font-semibold tracking-tight text-amber-900">
              Sospesi — campagne non riconosciute
            </h3>
            <Badge variant="secondary" className="ml-1 rounded-full bg-amber-200 text-amber-900">{unrecognized.length}</Badge>
            <span className="ml-auto text-xs font-medium text-amber-800/70">{suspOpen ? "nascondi" : "mostra"}</span>
          </button>
          {suspOpen && (
            <CardContent className="p-5 pt-0">
              <p className="mb-4 text-xs text-amber-800/80">
                Il sistema non è riuscito ad abbinare queste campagne a un corso. Quando una campagna riceve <strong>4 o più richieste in 15 giorni</strong>, il corso viene <strong>creato e abbinato automaticamente</strong>. Puoi comunque assegnarle a mano al corso corretto.
              </p>
              <div className="max-h-[28rem] space-y-2 overflow-auto">
                {unrecognized.map((c) => (
                  <div key={c.campaign} className="flex flex-col gap-2 rounded-lg border border-amber-200 bg-white p-3 sm:flex-row sm:items-center sm:justify-between" data-testid="unrecognized-campaign-row">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium" title={c.campaign}>{c.campaign}</p>
                      <p className="text-xs text-muted-foreground">
                        {c.count} lead
                        {typeof c.recent15 === "number" && (
                          <span className={c.recent15 >= 4 ? "ml-1 font-semibold text-amber-700" : "ml-1"}>
                            · {c.recent15} negli ultimi 15 gg
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <Select
                        value={assignSel[c.campaign] || ""}
                        onValueChange={(v) => setAssignSel((s) => ({ ...s, [c.campaign]: v }))}
                      >
                        <SelectTrigger className="w-56" data-testid="assign-course-select">
                          <SelectValue placeholder="Scegli corso…" />
                        </SelectTrigger>
                        <SelectContent>
                          {(courses || []).map((co) => (
                            <SelectItem key={co.id} value={co.id}>{co.official_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        size="sm"
                        data-testid="assign-campaign-button"
                        disabled={!assignSel[c.campaign] || assign.isPending}
                        onClick={() => assign.mutate({ campaign: c.campaign, course_id: assignSel[c.campaign] })}
                      >
                        Assegna
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label="Rimuovi dai sospesi"
                        title="Non assegnabile: rimuovi dai sospesi"
                        data-testid="ignore-campaign-button"
                        disabled={ignore.isPending}
                        onClick={() => ignore.mutate(c.campaign)}
                      >
                        <X className="h-4 w-4 text-rose-600" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      <Card className="shadow-sm" data-testid="campaigns-panel">
        <CardContent className="p-5">
          <div className="mb-1 flex items-center gap-2">
            <Tag className="h-4 w-4 text-blue-700" />
            <h3 className="font-display text-base font-semibold tracking-tight">Campagne rilevate nelle email</h3>
          </div>
          <p className="mb-4 text-xs text-muted-foreground">
            Nomi dei form/campagne estratti dalle email reali. Usali come riferimento per definire le parole chiave dei corsi.
          </p>
          <div className="max-h-96 overflow-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-secondary/80 backdrop-blur">
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-2">Campagna</th>
                  <th className="px-4 py-2 text-right">Lead</th>
                  <th className="px-4 py-2">Corso riconosciuto</th>
                </tr>
              </thead>
              <tbody>
                {(campaigns || []).map((c) => (
                  <tr key={c.campaign || "none"} className="border-t border-border hover:bg-secondary/40" data-testid="campaign-row">
                    <td className="px-4 py-2 font-medium">{c.campaign || <span className="text-muted-foreground">(vuota)</span>}</td>
                    <td className="px-4 py-2 text-right text-muted-foreground">{c.count}</td>
                    <td className="px-4 py-2">
                      {c.recognized
                        ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">{c.course_name}</span>
                        : <span className="rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-700">Non riconosciuto</span>}
                    </td>
                  </tr>
                ))}
                {(!campaigns || campaigns.length === 0) && (
                  <tr><td colSpan={3} className="px-4 py-8 text-center text-sm text-muted-foreground">Nessuna campagna disponibile.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
